import { useState } from "react";
import API from "../api/apiClient";
import { useNavigate } from "react-router-dom";

/* ===================== DISTRICTS ===================== */
const district = [
  "Gurugram",
  "Faridabad",
  "Sonipat",
  "Panipat",
  "Panchkula",
  "Sirsa",
  "Mahendergarh",
  "Kaithal",
  "Jind",
  "Jhajjar",
  "Fatehabad",
  "Bhiwani",
  "Palwal",
  "Nuh (Mewat)",
  "Kurukshetra",
  "Karnal",
  "Hisar",
  "Charkhi Dadri",
  "Ambala",
  "Yamunanagar",
  "Rohtak",
  "Rewari"
];

/* ===================== POLICIES ===================== */
const policies = [
  "DDJAY Plotting",
  "Retirement Housing",
  "Affordable Housing",
  "General Plotting",
  "General Group Housing 300PPA",
  "General Group Housing 400PPA",
  "General Commercial Colony 150 FAR",
  "General Commercial Colony 175 FAR",
  "General Industrial with 125FAR",
  "General Industrial use-IT with 250 FAR",
  "Industrial Plotting",
  "TOD"
];

const AREA_LIMITS = {
  "DDJAY Plotting": { min: 5, max: 15 },
  "Retirement Housing": { min: 0.5, max: 10 },
  "Affordable Housing": { min: 4, max: 30 },
};

/* ===================== FAR RULES ===================== */
const FAR_RULES = {
  "DDJAY Plotting": {
    residential: { type: "fixed", value: 1 },
    commercial: { type: "fixed", value: 1.5 }
  },
  "General Plotting": {
    residential: { type: "fixed", value: 1 },
    commercial: { type: "fixed", value: 1.5 }
  },
  "Industrial Plotting": {
    residential: { type: "fixed", value: 1 },
    commercial: { type: "fixed", value: 1.5 }
  },
  "Retirement Housing": {
    residential: { type: "fixed", value: 2.25 },
    commercial: { type: "fixed", value: 1.75 }
  },
  "Affordable Housing": {
    residential: { type: "fixed", value: 2.25 },
    commercial: { type: "fixed", value: 1.75 }
  },
  "General Group Housing 300PPA": {
    residential: { type: "options", values: [1.5, 1.75, 2.25, 3.5] },
    commercial: { type: "options", values: [1.5, 1.75] }
  },
  "General Group Housing 400PPA": {
    residential: { type: "options", values: [1.5, 1.75, 2.25, 3.5] },
    commercial: { type: "options", values: [1.5, 1.75] }
  },
  "General Commercial Colony 150 FAR": {
    commercial: { type: "fixed", value: 1.5 }
  },
  "General Commercial Colony 175 FAR": {
    commercial: { type: "fixed", value: 1.75 }
  },
  "General Industrial with 125FAR": {
    residential: { type: "options", values: [1.25, 1.5, 2.25] },
    commercial: { type: "fixed", value: 1.5 }
  },
  "General Industrial use-IT with 250 FAR": {
    residential: { type: "options", values: [2.5, 3.5] },
    commercial: { type: "options", values: [1.5, 1.75] }
  },
  "TOD": {
    residential: { type: "options", values: [2.5, 3.5] },
    commercial: { type: "options", values: [2.5, 3.5] }
  }
};

const CHARGE_TYPE = {
  edc: "Per Acre",
  idc: "₹ / Sqm",
  license: "Per Acre",
  conversion: "₹ / Sqm",
  scrutiny: "₹ / Sqm",
  idw: "Per Acre",
  iac: "₹ / Sqm",
};

function Cal() {
  const navigate = useNavigate();

  /* ===================== FORM ===================== */
  const [form, setForm] = useState({
    district: "",
    location: "",
    urbanEstate: "",
    potentialZone: "",
    policy: ""
  });

  const [locations, setLocations] = useState([]);

  /* ===================== DISTRICT CHANGE ===================== */
  const handleDistrictChange = async (e) => {
    const district = e.target.value;

    setForm({
      district,
      location: "",
      urbanEstate: "",
      potentialZone: "",
      policy: ""
    });

    setLocations([]);
    setRoadInfo({ road: "", roadType: "" });

    if (!district) return;

    const res = await API.get(
      `/locations/by-district?district=${district}`
    );

    setLocations(res.data);
  };

  /* ===================== LOCATION CHANGE ===================== */
  const handleLocationChange = (e) => {
    const location = e.target.value;
    const obj = locations.find(l => l.location === location);

    if (!obj) return;

    setForm(prev => ({
      ...prev,
      location,
      urbanEstate: obj.urbanEstate,
      potentialZone: obj.potentialZone
    }));

    setRoadInfo({ road: "", roadType: "" });
  };

  /* ===================== POLICY CHANGE ===================== */
  const handlePolicyChange = (e) => {
    const value = e.target.value;
    const rule = FAR_RULES[value] || {};

    setFar({
      residential:
        rule.residential?.type === "fixed" ? rule.residential.value : "",
      commercial:
        rule.commercial?.type === "fixed" ? rule.commercial.value : ""
    });

    setForm(prev => ({ ...prev, policy: value }));
  };

  const [roadInfo, setRoadInfo] = useState({
    road: "",
    roadType: ""
  });

  const [far, setFar] = useState({
    residential: "",
    commercial: ""
  });

  const [rates, setRates] = useState({
    residential: {},
    commercial: {}
  });

  const [area, setArea] = useState({
    totalAcre: "",
    commercialPercent: "",
  });

  const [areaError, setAreaError] = useState("");

  const validateArea = (value) => {
    if (value === "") {
      setAreaError("");
      return true;
    }

    const num = Number(value);
    if (num < 0) {
      setAreaError("Area cannot be negative");
      return false;
    }

    const rule = AREA_LIMITS[form.policy];
    if (!rule) return true;

    if (num < rule.min) {
      setAreaError(`Minimum area for ${form.policy} is ${rule.min} Acres`);
      return false;
    }

    if (num > rule.max) {
      setAreaError(`Maximum area for ${form.policy} is ${rule.max} Acres`);
      return false;
    }

    setAreaError("");
    return true;
  };

  const getCommercialPercentLimit = () => {
    if (!form.policy) return 0;
    if (form.policy === "TOD") return 30;
    if (
      [
        "General Commercial Colony 150 FAR",
        "General Commercial Colony 175 FAR",
      ].includes(form.policy)
    ) {
      return 100;
    }
    return 4;
  };

  const totalArea = Number(area.totalAcre || 0);
  const commercialPct = Number(area.commercialPercent || 0);

  const commercialArea =
    getCommercialPercentLimit() === 100
      ? totalArea
      : (totalArea * commercialPct) / 100;

  const residentialArea =
    getCommercialPercentLimit() === 100
      ? 0
      : totalArea - commercialArea;

  /* ===================== FETCH RATES ===================== */
  const fetchRates = async () => {
    if (!form.location || !form.policy) {
      alert("Select Location & Policy");
      return;
    }

    if (!roadInfo.roadType) {
      alert("Select Road Type");
      return;
    }

    if (form.urbanEstate === "Gurgaon" && !roadInfo.road) {
      alert("Select Road Name");
      return;
    }

    const payload = {
      policy: form.policy,
      urbanEstate: form.urbanEstate,
      potentialZone: form.potentialZone,
      roadType: roadInfo.roadType,
      road: form.urbanEstate === "Gurgaon" ? roadInfo.road : undefined
    };

    const res = await API.post("/get-all-charges", payload);
    setRates(res.data);
  };

  const goToResult = () => {
    if (!area.totalAcre || areaError) {
      alert(areaError || "Please enter total area");
      return;
    }

    navigate("/result", {
      state: {
        base: {
            district: form.district,
            location: form.location,
          urbanEstate: form.urbanEstate,
          potentialZone: form.potentialZone,
          policy: form.policy,
        },
        areaData: {
          residential: {
            area: residentialArea,
            far: Number(far.residential || 1),
          },
          commercial: {
            area: commercialArea,
            far: Number(far.commercial || 1),
          },
        },
        rates,
      },
    });
  };

  /* ===================== UI ===================== */
  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="bg-white p-5 rounded-xl shadow max-w-xl mx-auto mb-6">
        <h2 className="text-xl font-semibold mb-4 text-center">
          Charges Calculator
        </h2>

        <label>District</label>
        <select value={form.district} onChange={handleDistrictChange}
          className="w-full border p-2 mb-2">
          <option value="">Select</option>
          {district.map(d => <option key={d}>{d}</option>)}
        </select>

        <label>Location</label>
        <select value={form.location} onChange={handleLocationChange}
          disabled={!locations.length}
          className="w-full border p-2 mb-2">
          <option value="">Select Location</option>
          {locations.map(l => (
            <option key={l.location} value={l.location}>{l.location}</option>
          ))}
        </select>

        <label>Urban Estate</label>
        <input value={form.urbanEstate} readOnly
          className="w-full border p-2 mb-2 bg-gray-100" />

        <label>Potential Zone</label>
        <input value={form.potentialZone} readOnly
          className="w-full border p-2 mb-2 bg-gray-100" />

        <label>Policy</label>
        <select value={form.policy} onChange={handlePolicyChange}
          disabled={!form.location}
          className="w-full border p-2 mb-3">
          <option value="">Select</option>
          {policies.map(p => <option key={p}>{p}</option>)}
        </select>

        {form.urbanEstate === "Gurgaon" && (
          <>
            <label>Road Name</label>
            <select value={roadInfo.road}
              onChange={e => setRoadInfo(r => ({ ...r, road: e.target.value }))}
              className="w-full border p-2 mb-2">
              <option value="">Select Road</option>
              <option value="Mehroli Road">Mehroli Road</option>
              <option value="All Road">All Road</option>
            </select>
          </>
        )}

        <label>Road Type</label>
        <select value={roadInfo.roadType}
          onChange={e => setRoadInfo(r => ({ ...r, roadType: e.target.value }))}
          className="w-full border p-2 mb-3">
          <option value="">Select Road Type</option>
          <option>NH Road</option>
          <option>SR/Sector Road</option>
          <option>Other Road</option>
        </select>

        <button onClick={fetchRates}
          className="w-full bg-blue-600 text-white py-2 rounded">
          Get Rates
        </button>
      </div>

      <div className="bg-white p-5 rounded-xl shadow max-w-xl mx-auto mb-6">
        <h3 className="font-semibold mb-2">FAR</h3>

        <div className="grid grid-cols-2 gap-4">
          {["residential", "commercial"].map(type => {
            const rule = FAR_RULES[form.policy]?.[type];
            if (!rule) return null;

            return (
              <div key={type}>
                <label className="capitalize">{type}</label>

                {rule.type === "fixed" && (
                  <input readOnly value={rule.value}
                    className="w-full border p-2 bg-gray-100" />
                )}

                {rule.type === "options" && (
                  <select
                    value={far[type]}
                    onChange={e =>
                      setFar(f => ({ ...f, [type]: e.target.value }))
                    }
                    className="w-full border p-2"
                  >
                    <option value="">Select FAR</option>
                    {rule.values.map(v => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white p-5 rounded-xl shadow max-w-xl mx-auto mb-6">
  <h3 className="font-semibold mb-3">Area Distribution (Acre)</h3>

  {/* TOTAL AREA */}
  <label>Total Area (Acre)</label>
<input
  type="number"
  step="0.01"
  value={area.totalAcre}
  onChange={(e) => {
    const val = e.target.value;
    setArea((a) => ({ ...a, totalAcre: val }));
    validateArea(val);
  }}
  className="w-full border p-2 mb-1"
/>

{areaError && (
  <div className="text-red-600 text-sm mt-1">
    {areaError}
  </div>
)}


  {/* COMMERCIAL % */}
  {getCommercialPercentLimit() !== 100 && (
    <>
      <label>
        Commercial Percentage (0 – {getCommercialPercentLimit()}%)
      </label>
      <input
  type="number"
  value={area.commercialPercent}
  onChange={(e) => {
    let val = Number(e.target.value);
    const max = getCommercialPercentLimit();

    if (isNaN(val)) val = "";
    else if (val < 0) val = 0;
    else if (val > max) val = max;

    setArea((a) => ({ ...a, commercialPercent: val }));
  }}
  className="w-full border p-2 mb-3"
/>

    </>
  )}

  {/* CALCULATED AREAS */}
  <div className="grid grid-cols-2 gap-4 text-sm">
    <div className="bg-gray-100 p-3 rounded">
      <b>Commercial Area</b>
      <div>{commercialArea.toFixed(2)} Acre</div>
    </div>

    <div className="bg-gray-100 p-3 rounded">
      <b>Residential / Industrial Area</b>
      <div>{residentialArea.toFixed(2)} Acre</div>
    </div>
  </div>
</div>


      <div className="bg-white p-5 rounded-xl shadow max-w-xl mx-auto">
        <h3 className="font-semibold mb-2">Charges (Rates)</h3>
        <table className="w-full border text-sm">
          <thead className="bg-green-100">
            <tr>
              <th className="border p-2">Charge</th>
              <th className="border p-2">Type</th>
              <th className="border p-2">Residential/Industrial</th>
              <th className="border p-2">Commercial</th>
            </tr>
          </thead>
          <tbody>
            {["edc","idc","license","conversion","scrutiny","idw","iac"].map(key => (
              <tr key={key}>
                <td className="border p-2">{key.toUpperCase()}</td>
                <td className="border p-2 text-center">{CHARGE_TYPE[key]}</td>
                <td className="border p-2">{rates.residential?.[key] ?? 0}</td>
                <td className="border p-2">{rates.commercial?.[key] ?? 0}</td>
                
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="max-w-xl mx-auto mt-6">
  <button
    onClick={goToResult}
    className="w-full bg-emerald-600 text-white py-3 rounded-lg text-lg hover:bg-emerald-700 transition"
  >
    Calculate & View Result
  </button>
</div>


    </div>
  );
}

export default Cal;
